/**
 * Timezone utilities for intake callability.
 *
 * Maps US state abbreviations to IANA timezones so the frontend can determine
 * whether a PNC is within callable hours without a backend round-trip.
 *
 * Callable window: 9:00 AM – 7:00 PM in the PNC's local timezone.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TimezoneAbbr = 'EST' | 'CST' | 'MST' | 'PST' | 'AKST' | 'HST';

export interface CallabilityInfo {
  /** Whether the PNC is within callable hours right now */
  isCallable: boolean;
  /** Display abbreviation (e.g. "PST") */
  tzAbbrev: TimezoneAbbr;
  /** PNC's current local time formatted for display (e.g. "7:42 AM") */
  localTime: string;
  /** PNC's state full name (e.g. "Washington") */
  stateName: string;
  /**
   * If not yet callable, the next time the window opens (formatted in the
   * viewer's local time, e.g. "12:00 PM"). null if currently callable.
   */
  opensAt: string | null;
  /**
   * If not yet callable, the raw Date when the window opens. null if callable.
   */
  opensAtDate: Date | null;
}

// ---------------------------------------------------------------------------
// State → IANA timezone mapping
// ---------------------------------------------------------------------------

/**
 * Maps state abbreviations to their IANA timezone identifier.
 * States that span two timezones use their majority/capital timezone.
 */
const STATE_TO_IANA: Record<string, string> = {
  // Eastern
  CT: 'America/New_York',
  DC: 'America/New_York',
  DE: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  IN: 'America/New_York',
  MA: 'America/New_York',
  MD: 'America/New_York',
  ME: 'America/New_York',
  MI: 'America/New_York',
  NC: 'America/New_York',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NY: 'America/New_York',
  OH: 'America/New_York',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  VA: 'America/New_York',
  VT: 'America/New_York',
  WV: 'America/New_York',
  // Central
  AL: 'America/Chicago',
  AR: 'America/Chicago',
  IA: 'America/Chicago',
  IL: 'America/Chicago',
  KS: 'America/Chicago',
  KY: 'America/Chicago',
  LA: 'America/Chicago',
  MN: 'America/Chicago',
  MO: 'America/Chicago',
  MS: 'America/Chicago',
  NE: 'America/Chicago',
  ND: 'America/Chicago',
  OK: 'America/Chicago',
  SD: 'America/Chicago',
  TN: 'America/Chicago',
  TX: 'America/Chicago',
  WI: 'America/Chicago',
  // Mountain
  AZ: 'America/Phoenix', // No DST
  CO: 'America/Denver',
  ID: 'America/Boise',
  MT: 'America/Denver',
  NM: 'America/Denver',
  UT: 'America/Denver',
  WY: 'America/Denver',
  // Pacific
  CA: 'America/Los_Angeles',
  NV: 'America/Los_Angeles',
  OR: 'America/Los_Angeles',
  WA: 'America/Los_Angeles',
  // Non-contiguous
  AK: 'America/Anchorage',
  HI: 'Pacific/Honolulu',
};

/**
 * Maps IANA timezone identifiers to display abbreviations.
 * Uses a representative date to handle DST automatically, but we also keep
 * a static fallback so the mapping works even without Intl support.
 */
const IANA_TO_ABBREV: Record<string, TimezoneAbbr> = {
  'America/New_York': 'EST',
  'America/Chicago': 'CST',
  'America/Denver': 'MST',
  'America/Boise': 'MST',
  'America/Phoenix': 'MST',
  'America/Los_Angeles': 'PST',
  'America/Anchorage': 'AKST',
  'Pacific/Honolulu': 'HST',
};

// Reverse lookup: abbreviation → full state name (for tooltip)
const STATE_ABBREV_TO_NAME: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

// ---------------------------------------------------------------------------
// Callable window configuration
// ---------------------------------------------------------------------------

/** Earliest hour to call (inclusive), in PNC's local time */
const CALLABLE_START_HOUR = 9; // 9:00 AM

/** Latest hour to call (exclusive), in PNC's local time */
const CALLABLE_END_HOUR = 19; // 7:00 PM

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// Full state name → abbreviation (for DB records that store "California" instead of "CA")
const STATE_NAME_TO_ABBREV: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_ABBREV_TO_NAME).map(([abbr, name]) => [
    name.toLowerCase(),
    abbr,
  ]),
);

/**
 * Normalize a state value (abbreviation or full name) to an uppercase abbreviation.
 * Returns undefined if not recognized.
 */
function normalizeState(state: string): string | undefined {
  const trimmed = state.trim();
  const upper = trimmed.toUpperCase();
  if (STATE_TO_IANA[upper]) return upper;
  // Try full name lookup
  const fromName = STATE_NAME_TO_ABBREV[trimmed.toLowerCase()];
  return fromName;
}

/**
 * Get the IANA timezone for a US state (abbreviation or full name).
 * Returns undefined if the state is not recognized.
 */
export function getIanaTimezone(stateAbbrev: string): string | undefined {
  const abbr = normalizeState(stateAbbrev);
  return abbr ? STATE_TO_IANA[abbr] : undefined;
}

/**
 * Get the display abbreviation (EST, CST, etc.) for a US state (abbreviation or full name).
 * Returns undefined if the state is not recognized.
 */
export function getTimezoneAbbrev(
  stateAbbrev: string,
): TimezoneAbbr | undefined {
  const abbr = normalizeState(stateAbbrev);
  if (!abbr) return undefined;
  const iana = STATE_TO_IANA[abbr];
  if (!iana) return undefined;
  return IANA_TO_ABBREV[iana];
}

/**
 * Get the current local time in a given IANA timezone, formatted for display.
 */
function getLocalTime(iana: string, now: Date): string {
  return now.toLocaleTimeString('en-US', {
    timeZone: iana,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get the current hour in a given IANA timezone (0-23).
 */
function getLocalHour(iana: string, now: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    hour: 'numeric',
    hour12: false,
  }).formatToParts(now);
  const hourPart = parts.find((p) => p.type === 'hour');
  return parseInt(hourPart?.value ?? '0', 10);
}

/**
 * Compute full callability info for an intake given its PNC state.
 *
 * @param stateAbbrev - Two-letter US state abbreviation (e.g. "WA")
 * @param now - Optional: override current time (useful for testing)
 * @returns CallabilityInfo, or null if state is not recognized
 */
export function getCallabilityInfo(
  stateAbbrev: string | undefined | null,
  now: Date = new Date(),
): CallabilityInfo | null {
  if (!stateAbbrev) return null;

  const abbr = normalizeState(stateAbbrev);
  if (!abbr) return null;

  const iana = STATE_TO_IANA[abbr];
  if (!iana) return null;

  const tzAbbrev = IANA_TO_ABBREV[iana];
  const localTime = getLocalTime(iana, now);
  const localHour = getLocalHour(iana, now);
  const stateName = STATE_ABBREV_TO_NAME[abbr] || abbr;

  const isCallable =
    localHour >= CALLABLE_START_HOUR && localHour < CALLABLE_END_HOUR;

  let opensAt: string | null = null;
  let opensAtDate: Date | null = null;

  if (!isCallable && localHour < CALLABLE_START_HOUR) {
    // Before the callable window — compute when it opens in viewer's local time.
    // Build a Date for CALLABLE_START_HOUR:00 in the PNC's timezone.
    const localDateStr = now.toLocaleDateString('en-CA', { timeZone: iana }); // YYYY-MM-DD
    const targetLocal = new Date(`${localDateStr}T${String(CALLABLE_START_HOUR).padStart(2, '0')}:00:00`);

    // Compute offset between PNC timezone and UTC
    const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC' });
    const pncStr = now.toLocaleString('en-US', { timeZone: iana });
    const utcDate = new Date(utcStr);
    const pncDate = new Date(pncStr);
    const offsetMs = pncDate.getTime() - utcDate.getTime();

    // Target time in UTC, then convert to viewer's local time
    const targetUtc = new Date(targetLocal.getTime() - offsetMs);
    opensAtDate = targetUtc;
    opensAt = targetUtc.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return {
    isCallable,
    tzAbbrev,
    localTime,
    stateName,
    opensAt,
    opensAtDate,
  };
}

/**
 * All unique timezone abbreviations in display order (East → West).
 */
export const TIMEZONE_DISPLAY_ORDER: TimezoneAbbr[] = [
  'EST',
  'CST',
  'MST',
  'PST',
  'AKST',
  'HST',
];

/**
 * Filter options for the TZ column in the intake table.
 */
export const TIMEZONE_FILTER_OPTIONS = TIMEZONE_DISPLAY_ORDER.map((tz) => ({
  label: tz,
  value: tz,
}));
